import { ExternalLink, Settings, Gift, ChevronDown } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import StoreBadges, { STORE_LINKS } from './StoreBadges';
import { trackEvent } from '@/lib/analytics';

const APPS = [
  {
    id: 'admin',
    title: 'Tilki Yönetim',
    audience: 'İşletmeler için',
    icon: Settings,
    url: 'https://app.tilkiotomasyon.com',
    action: 'Yönetim panelini aç',
    description: 'Masa, sipariş ve satışlarınızı yönetin. Webden giriş yapın veya mobil uygulamayı indirin.',
  },
  {
    id: 'loyalty',
    title: 'Tilki Sadakat',
    audience: 'Müşteriler için',
    icon: Gift,
    url: 'https://sadakat.tilkiotomasyon.com',
    action: 'Sadakat uygulamasını aç',
    description:
      'Puanlarınızı, kampanyalarınızı ve ödüllerinizi takip edin. Webden erişin veya mobil uygulamayı indirin.',
  },
];
const trackAccess = (app) => trackEvent('app-link-click', { app });

function AccessOptions() {
  return (
    <div className="space-y-4">
      {APPS.map((app) => (
        <div key={app.id} className="rounded-xl border border-hairline p-4">
          <p className="text-xs font-medium text-brand-700">{app.audience}</p>
          <p className="mt-1 font-semibold text-ink">{app.title}</p>
          <a
            href={app.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAccess(app.title)}
            className="mt-2 flex min-h-11 items-center gap-2 text-sm font-semibold text-ink underline underline-offset-4"
          >
            {app.action}
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-muted">
            <a
              className="inline-flex min-h-11 items-center underline underline-offset-4"
              href={STORE_LINKS[app.id].ios}
              target="_blank"
              rel="noopener noreferrer"
            >
              App Store’dan indir
            </a>
            <a
              className="inline-flex min-h-11 items-center underline underline-offset-4"
              href={STORE_LINKS[app.id].android}
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Play’den indir
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}

export function DesktopAppAccess() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex min-h-11 items-center gap-1 whitespace-nowrap text-sm font-semibold text-ink hover:text-brand-700"
        >
          Giriş ve Uygulamalar <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 max-h-[calc(100dvh-6rem)] overflow-y-auto rounded-2xl bg-surface p-3">
        <AccessOptions />
      </PopoverContent>
    </Popover>
  );
}

export function MobileAppAccess() {
  return (
    <details className="rounded-xl border border-hairline p-3">
      <summary className="cursor-pointer py-2 font-semibold text-ink">Giriş ve Uygulamalar</summary>
      <div className="mt-2">
        <AccessOptions />
      </div>
    </details>
  );
}

export default function AppAccess() {
  return (
    <section id="applications" className="section-y scroll-mt-24 bg-surface" aria-labelledby="applications-title">
      <div className="container mx-auto px-4">
        <div className="mb-8 max-w-2xl">
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-brand-700">
            Web ve mobil uygulamalar
          </p>
          <h2 id="applications-title" className="type-h2">
            Tilki’ye her yerden ulaşın
          </h2>
          <p className="mt-4 leading-relaxed text-ink-muted">
            İşletmenizi yönetmek veya sadakat hesabınıza ulaşmak için size uygun uygulamayı seçin.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {APPS.map((app) => (
            <article
              key={app.id}
              className="flex min-w-0 flex-col rounded-2xl border border-hairline bg-surface-2 p-6 sm:p-8"
            >
              <div className="mb-4 flex items-center gap-3 text-brand-700">
                <app.icon className="h-6 w-6" aria-hidden="true" />
                <span className="text-sm font-semibold">{app.audience}</span>
              </div>
              <h3 className="font-poppins text-2xl font-semibold text-ink">{app.title}</h3>
              <p className="mt-3 mb-6 flex-1 leading-relaxed text-ink-muted">{app.description}</p>
              <a
                href={app.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackAccess(app.title)}
                className="modern-btn-outline w-full bg-surface"
              >
                {app.action}
                <ExternalLink className="ml-2 h-4 w-4" aria-hidden="true" />
              </a>
              <StoreBadges apps={app.id} className="mt-6 border-t border-hairline pt-5" />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
